import { useInfiniteQuery } from "@tanstack/react-query";
import type { StoredEvent } from "@facade/dashboard-shared";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { EventRow, KindChips, NAMESPACES } from "../components/EventStream";
import { ModelQueryDrawer } from "../components/ModelQueryDrawer";
import { api } from "../lib/api";
import { mergeEvents, useLive } from "../lib/ws";

export function Events() {
  const { deviceId = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const [ns, setNs] = useState<Set<string>>(new Set((sp.get("ns") ?? "").split(",").filter(Boolean)));
  const [kind, setKind] = useState(sp.get("kind") ?? "");
  const [search, setSearch] = useState(sp.get("q") ?? "");
  const [runId, setRunId] = useState(sp.get("run") ?? "");
  const [applied, setApplied] = useState({ kind, search, runId, ns });
  const [liveOn, setLiveOn] = useState(true);
  const [query, setQuery] = useState<StoredEvent | null>(null);

  const kinds = applied.kind ? [applied.kind] : applied.ns.size ? [...applied.ns].map((n) => `${n}.*`) : undefined;
  const hist = useInfiniteQuery({
    queryKey: ["events", deviceId, applied],
    queryFn: ({ pageParam }) => api.events(deviceId, { kinds, runId: applied.runId || undefined, search: applied.search || undefined, cursor: pageParam as number | null, limit: 100 }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const live = useLive({ deviceId, runId: applied.runId || undefined, kinds }, { limit: 100, enabled: liveOn && !applied.search });
  const events = useMemo(() => {
    const h = (hist.data?.pages ?? []).flatMap((p) => p.events);
    return mergeEvents(h, live).reverse();
  }, [hist.data, live]);

  const apply = () => {
    setApplied({ kind, search, runId, ns });
    const next = new URLSearchParams();
    if (kind) next.set("kind", kind);
    if (search) next.set("q", search);
    if (runId) next.set("run", runId);
    if (ns.size) next.set("ns", [...ns].join(","));
    setSp(next, { replace: true });
  };

  return (
    <div className="stack">
      <h1>Events</h1>
      <div className="panel">
        <div className="toolbar">
          <KindChips value={ns} onChange={(v) => { setNs(v); setKind(""); }} all={[...NAMESPACES, "telemetry"]} />
        </div>
        <div className="toolbar">
          <input type="text" placeholder="exact kind (e.g. model.query)" value={kind} onChange={(e) => setKind(e.target.value)} list="kinds" />
          <datalist id="kinds">
            {["run.start", "run.end", "run.step", "model.query", "screen.observe", "screen.shot", "graph.state", "graph.edge", "graph.edge.remove", "graph.snapshot", "loop.tick", "feed.sample", "capability.learned", "device.hello"].map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
          <input type="text" placeholder="run id" value={runId} onChange={(e) => setRunId(e.target.value)} />
          <input type="text" placeholder="payload contains…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} />
          <button className="primary" onClick={apply}>
            apply
          </button>
          <label className="muted" style={{ fontSize: 12 }}>
            <input type="checkbox" checked={liveOn} onChange={(e) => setLiveOn(e.target.checked)} disabled={!!applied.search} /> live
          </label>
          <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
            {events.length} loaded
          </span>
        </div>
        {events.length === 0 ? (
          <div className="empty">{hist.isLoading ? "loading…" : "no events"}</div>
        ) : (
          <div className="stream">
            {events.map((e) => (
              <EventRow key={`${e.deviceId}|${e.seq}`} e={e} deviceId={deviceId} onOpenQuery={setQuery} />
            ))}
          </div>
        )}
        {hist.hasNextPage && (
          <button style={{ marginTop: 8 }} onClick={() => hist.fetchNextPage()} disabled={hist.isFetchingNextPage}>
            {hist.isFetchingNextPage ? "loading…" : "load older"}
          </button>
        )}
      </div>
      {query && <ModelQueryDrawer e={query} onClose={() => setQuery(null)} />}
    </div>
  );
}
